import { Component, Input, Output, EventEmitter, ViewChild, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { provideNativeDateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { QuickRange } from '@app/shared/types/IntervalPicker.types';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'app-advanced-interval-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }
  ],
  templateUrl: './advanced-interval-picker.component.html',
  styleUrls: ['./advanced-interval-picker.component.css']
})
export class AdvancedIntervalPickerComponent implements OnInit, OnChanges {
  @Input() initialFrom: Date | null = null;
  @Input() initialTo: Date | null = null;
  @Input() quickRanges: QuickRange[] = [];
  @Output() intervalChanged = new EventEmitter<{ from: Date; to: Date }>();
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  currentFrom: Date | null = null;
  currentTo: Date | null = null;

  tempFrom: Date | null = null;
  tempTo: Date | null = null;
  activeRangeLabel: string | null = null;

  ngOnInit() {
    this.syncInitialValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialFrom'] || changes['initialTo']) {
      this.syncInitialValues();
    }
  }

  private syncInitialValues() {
    this.currentFrom = this.initialFrom;
    this.currentTo = this.initialTo;
    this.evaluateActiveRange();
  }

  private evaluateActiveRange() {
    if (!this.currentFrom || !this.currentTo) {
      this.activeRangeLabel = null;
      return;
    }

    const matchedRange = this.quickRanges.find(range => {
      const { from, to } = range.calculate();
      return this.isSameDate(from, this.currentFrom!) && this.isSameDate(to, this.currentTo!);
    });

    this.activeRangeLabel = matchedRange ? matchedRange.label : null;
  }

  private isSameDate(d1: Date, d2: Date): boolean {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  get displayText(): string {
    if (!this.currentFrom || !this.currentTo) return 'Izaberite period';
    return `${this.formatDate(this.currentFrom)} - ${this.formatDate(this.currentTo)}`;
  }

  onMenuOpened() {
    this.tempFrom = this.currentFrom ? new Date(this.currentFrom) : null;
    this.tempTo = this.currentTo ? new Date(this.currentTo) : null;
  }

  selectQuickRange(range: QuickRange) {
    const { from, to } = range.calculate();
    this.currentFrom = from;
    this.currentTo = to;
    this.activeRangeLabel = range.label;
    this.intervalChanged.emit({ from, to });
    this.menuTrigger.closeMenu();
  }

  applyManualRange() {
    if (this.tempFrom && this.tempTo && this.tempFrom.getTime() <= this.tempTo.getTime()) {

      const from = new Date(this.tempFrom);
      from.setHours(0, 0, 0, 0);

      const to = new Date(this.tempTo);
      to.setHours(23, 59, 59, 999);

      this.currentFrom = from;
      this.currentTo = to;
      this.activeRangeLabel = null;
      this.intervalChanged.emit({ from, to });
      this.menuTrigger.closeMenu();
    }
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
}